// The New delivery form as plain data: validation, the one shipment every option is quoted for, the own-driver
// payload, and prefill for "Copy as new delivery". Field names match the inputs' ids (md-field-<name>).
import { STORE_PROVIDER } from "shared/constants";
import {
  addDays,
  addMinutes,
  formatClock,
  startOptionsFor,
  startTimeOptions,
  toMinutes,
  WINDOW_MINUTES,
} from "./manualDeliveryTime";

export const DEFAULT_START = "13:00";
// The bags/boxes stepper runs 1 to 12. The server's sanity cap (Shipment::MAX_PACKAGE_COUNT, 100) and carrier
// limits still apply to its validation messages.
export const MAX_PACKAGES = 12;
export const FIELD_ORDER = [
  "first",
  "line1",
  "city",
  "province",
  "postal",
  "value",
  "date",
  "start",
  "weight",
];

const START_VALUES = startTimeOptions().map((o) => o.value);

export function blankForm(todayIso) {
  return {
    first: "",
    last: "",
    phone: "",
    email: "",
    line1: "",
    line2: "",
    city: "",
    province: "",
    postal: "",
    value: "",
    items: "",
    date: todayIso,
    start: DEFAULT_START,
    count: 1,
    weight: "",
    alcohol: false,
    signature: false,
    pickupNotes: "",
    choice: "own",
  };
}

// The stepper's top: 12, or a courier's own package limit when that is lower.
export function packageMax(carrierMax) {
  return carrierMax > 0 ? Math.min(MAX_PACKAGES, carrierMax) : MAX_PACKAGES;
}

// Dollars with at most two decimals, after dropping "$", commas and spaces; NaN for anything else ("1e3", "0x10",
// "Infinity", "1.005").
export function parseMoney(text) {
  const plain = String(text || "").replace(/[$,\s]/g, "");
  return /^\d+(\.\d{1,2})?$/.test(plain) ? Number(plain) : NaN;
}

// now: the shop's { date, hhmm } (shopNow), or null to skip the "already passed" check.
export function validateForm(form, now) {
  const errors = {};
  if (!`${form.first} ${form.last}`.trim()) {
    errors.first = "Enter the customer's name";
  }
  if (!form.line1.trim()) errors.line1 = "Enter a street address";
  if (!form.city.trim()) errors.city = "Enter a city";
  if (!form.province.trim()) errors.province = "Enter a province";
  if (!form.postal.trim()) errors.postal = "Enter a postal code";
  if (!(parseMoney(form.value) > 0)) errors.value = "Enter an amount above $0";
  // ISO dates compare as strings. A form left open past midnight can hold yesterday.
  if (now && form.date < now.date) {
    errors.date = "Pick today or a later date";
  } else if (
    now &&
    form.date === now.date &&
    toMinutes(form.start) <= toMinutes(now.hhmm)
  ) {
    errors.start = "Pick a start time later than now";
  }
  if (form.weight.trim() && !(Number(form.weight) > 0)) {
    errors.weight = "Enter a weight above 0 kg, or leave it blank";
  }
  return errors;
}

export function firstErrorField(errors) {
  return FIELD_ORDER.find((key) => errors[key]) || null;
}

// Only keys in ManualDeliveries::Shipment::PERMITTED. One shipment for every option, so a price, its quote token and
// the booking all describe the same delivery. Dimensions are not collected (the server still accepts them).
export function shipmentFromForm(form, timezone) {
  const shipment = {
    package_count: form.count,
    value_cents: Math.round(parseMoney(form.value) * 100) || 0,
    contains_alcohol: !!form.alcohol,
    signature_required: !!form.signature,
    notes: form.items.trim(),
    pickup_notes: form.pickupNotes.trim(),
    contact: {
      name: `${form.first} ${form.last}`.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
    },
    address: {
      line1: form.line1.trim(),
      line2: form.line2.trim(),
      city: form.city.trim(),
      province: form.province.trim(),
      postal: form.postal.trim(),
    },
    window: {
      date: form.date,
      start_time: form.start,
      end_time: addMinutes(form.start, WINDOW_MINUTES),
      timezone,
    },
  };
  const weight = Number(form.weight);
  if (form.weight.trim() && weight > 0) shipment.total_weight_kg = weight;
  return shipment;
}

// shipmentFromForm always builds keys in the same order, so equal shipments give equal keys. The carrier choice is
// not part of it: one answer prices every option for the shipment.
export function shipmentKey(shipment) {
  return JSON.stringify(shipment);
}

// Any edit since the panel opened (base is the form it opened with), so closing asks before discarding it.
export function isFormDirty(form, base) {
  return Object.keys(base).some((key) => form[key] !== base[key]);
}

export function ownDriverPayload(form, timezone) {
  const shipment = shipmentFromForm(form, timezone);
  const a = shipment.address;
  return {
    delivery_date: form.date,
    start_time: form.start,
    total_value_cents: shipment.value_cents,
    items_notes: shipment.notes,
    carrier_provider: STORE_PROVIDER,
    status: "pending",
    customer_name: shipment.contact.name,
    customer_email: shipment.contact.email,
    customer_phone: shipment.contact.phone,
    address: [a.line1, a.line2, a.city, a.province, a.postal]
      .filter(Boolean)
      .join(", "),
    address_details: {
      address1: a.line1,
      address2: a.line2,
      city: a.city,
      province: a.province,
      postal_code: a.postal,
    },
    client_timezone: timezone,
  };
}

// Prefill for "Copy as new delivery": same customer, address, order and packages, for today. Dimensions are
// dropped (the panel does not show them).
export function formFromRow(row, todayIso) {
  const shipment = row.shipment || {};
  const extra = row.extra || {};
  const details = extra.address_details || {};
  const contact = shipment.contact || {};
  const address = shipment.address || {};
  const name = (contact.name || extra.customer_name || "").trim();
  const [first, ...rest] = name.split(/\s+/);
  const start =
    (shipment.window && shipment.window.start_time) || row.start_time;
  let choice = "own";
  if (!row.own_driver && row.carrier_id) choice = `carrier-${row.carrier_id}`;
  return {
    ...blankForm(todayIso),
    first: first || "",
    last: rest.join(" "),
    phone: contact.phone || extra.customer_phone || "",
    email: contact.email || extra.customer_email || "",
    line1: address.line1 || details.address1 || "",
    line2: address.line2 || details.address2 || "",
    city: address.city || details.city || "",
    province: address.province || details.province || "",
    postal: address.postal || details.postal_code || "",
    value: ((row.total_value_cents || 0) / 100).toFixed(2),
    items: shipment.notes || row.items_notes || "",
    start: START_VALUES.includes(start) ? start : DEFAULT_START,
    count: Math.min(MAX_PACKAGES, shipment.package_count || 1),
    weight: shipment.total_weight_kg ? String(shipment.total_weight_kg) : "",
    alcohol: !!shipment.contains_alcohol,
    signature: !!shipment.signature_required,
    pickupNotes: shipment.pickup_notes || "",
    choice,
  };
}

// The first start still open on dateIso, or null. Only the shop's today has closed starts here; an earlier date is
// left to validateForm.
function firstOpenStart(dateIso, now) {
  const open = startOptionsFor(dateIso, now).find((option) => !option.disabled);
  return open ? open.value : null;
}

const startClosed = (form, now) =>
  !!now &&
  form.date === now.date &&
  startOptionsFor(form.date, now).some((option) => option.value === form.start && option.disabled);

// The form a panel opens with (blank or copied): a start that has already passed today moves to the first open
// one, or to tomorrow at the default start when none is left. Anything else is returned as it is.
export function withOpenStart(form, now) {
  if (!startClosed(form, now)) return form;
  const start = firstOpenStart(form.date, now);
  return start
    ? { ...form, start }
    : { ...form, date: addDays(now.date, 1), start: DEFAULT_START };
}

// A new delivery date. The start moves only when this change made it a passed time on today (and a later one is
// left); a start staff chose is otherwise kept, and the start error explains a passed one.
export function withDate(form, dateIso, now) {
  const next = { ...form, date: dateIso };
  if (!startClosed(next, now)) return next;
  const start = firstOpenStart(dateIso, now);
  return start ? { ...next, start } : next;
}

// A carrier's one-click fix (QuoteAll option.fix). Today only a start time.
export function applyFix(form, fix) {
  if (!fix || !fix.start_time) return form;
  return { ...form, start: fix.start_time };
}

export function fixLabel(fix) {
  return fix && fix.start_time
    ? `Use ${formatClock(fix.start_time)} start`
    : "";
}
